// Automation pages (scheduled runs / event hooks) + their inline settings
// (WEB-8004). Routines feature styles (WEB-7961 folder). Composed by
// workspaceStyles.ts in place.
export const routinesAutomationPages = `
/* ===== Automation pages (scheduled / hooks) ===== */
.ws-auto-page {
  padding: 32px 40px;
  max-width: 960px;
  margin: 0 auto;
  width: 100%;
}
.ws-auto-section-head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ws-ink-4);
  margin-bottom: 14px;
}
.ws-auto-new-btn {
  margin-left: auto;
  text-transform: none;
  letter-spacing: 0;
  font-size: 13px;
}
.ws-auto-empty {
  padding: 40px 20px;
  text-align: center;
  color: var(--ws-ink-4);
  font-size: 14px;
}
.ws-auto-row {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--ws-line);
  border-radius: var(--ws-r-md);
  margin-bottom: 8px;
  cursor: pointer;
  background: var(--ws-panel);
  transition: border-color 0.15s, box-shadow 0.15s;
}
.ws-auto-row:hover { border-color: var(--ws-line-2); }
.ws-auto-row-expanded {
  border-color: var(--ws-line-2);
  box-shadow: var(--ws-shadow-sm);
  cursor: default;
}
.ws-auto-row-head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 16px;
  /* The head is always the clickable collapse/expand target — keep the pointer
     even when the row is expanded (the row sets cursor:default for its body). */
  cursor: pointer;
}
.ws-auto-row-disabled { opacity: 0.55; cursor: default; }
.ws-auto-icon {
  width: 30px;
  height: 30px;
  border-radius: 7px;
  display: grid;
  place-items: center;
  background: rgba(74,107,133,0.1);
  color: var(--ws-a-forecasting);
  flex-shrink: 0;
}
.ws-auto-icon-hook {
  background: rgba(95,127,106,0.1);
  color: var(--ws-a-prospecting);
}
.ws-auto-info {
  flex: 1;
  min-width: 0;
}
.ws-auto-label {
  font-weight: 500;
  font-size: 15px;
  color: var(--ws-ink);
}
.ws-auto-meta {
  font-size: 13px;
  color: var(--ws-ink-4);
  margin-top: 2px;
}
.ws-auto-runs-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: var(--ws-r-sm);
  border: none;
  background: transparent;
  color: var(--ws-ink-4);
  font-size: 12px;
  font-weight: 500;
  font-family: var(--ws-sans);
  cursor: pointer;
  transition: color 0.12s, background 0.12s;
  flex-shrink: 0;
}
.ws-auto-runs-link:hover { color: var(--ws-ink-2); background: var(--ws-soft); }
/* Not-yet-available action (e.g. Compile before the routine has run): visibly
   inert — dimmed, default cursor, and no hover highlight — but still hoverable
   so its title tooltip explains why. */
.ws-auto-runs-link--muted { opacity: 0.4; cursor: default; }
.ws-auto-runs-link--muted:hover { color: var(--ws-ink-4); background: transparent; }
/* Playbook affordance gets a fixed slot so its two label states
   ("Set up Playbook" / "Playbook") occupy identical width — this keeps the
   whole action cluster aligned across rows regardless of the label. */
.ws-auto-runs-link--playbook { min-width: 136px; justify-content: flex-start; }
.ws-auto-toggle {
  position: relative;
  width: 36px;
  height: 20px;
  border-radius: 10px;
  border: none;
  background: var(--ws-line-2);
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s;
  padding: 0;
}
.ws-auto-toggle.on { background: var(--ws-ink); }
.ws-auto-toggle-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15);
  transition: transform 0.2s;
}
.ws-auto-toggle.on .ws-auto-toggle-knob { transform: translateX(16px); }

/* ===== Automation inline settings ===== */
.ws-auto-settings {
  border-top: 1px solid var(--ws-line);
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.ws-auto-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.ws-auto-field-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ws-ink-4);
}
.ws-auto-input {
  background: var(--ws-panel);
  border: 1px solid var(--ws-line-2);
  border-radius: var(--ws-r-sm);
  padding: 9px 12px;
  font-size: 14px;
  color: var(--ws-ink);
  outline: none;
  font-family: var(--ws-sans);
  transition: border-color 0.15s;
}
.ws-auto-input:focus { border-color: var(--ws-ink-3); }
.ws-auto-textarea {
  background: var(--ws-panel);
  border: 1px solid var(--ws-line-2);
  border-radius: var(--ws-r-sm);
  padding: 9px 12px;
  font-size: 13.5px;
  line-height: 1.55;
  color: var(--ws-ink);
  outline: none;
  font-family: var(--ws-sans);
  resize: vertical;
  min-height: 80px;
  transition: border-color 0.15s;
}
.ws-auto-textarea:focus { border-color: var(--ws-ink-3); }
.ws-auto-trigger-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: var(--ws-soft);
  border: 1px solid var(--ws-line);
  border-radius: var(--ws-r-sm);
  font-size: 13px;
  color: var(--ws-ink-2);
  font-weight: 500;
  width: fit-content;
  text-transform: capitalize;
}
.ws-auto-filters {
  background: var(--ws-soft);
  border: 1px solid var(--ws-line);
  border-radius: var(--ws-r-sm);
  padding: 9px 12px;
  font-size: 13px;
  color: var(--ws-ink-3);
  line-height: 1.5;
}
.ws-auto-scope-tabs {
  display: inline-flex;
  gap: 3px;
  background: var(--ws-soft);
  border: 1px solid var(--ws-line-2);
  border-radius: var(--ws-r-sm);
  padding: 3px;
  width: fit-content;
}
.ws-auto-scope-tab {
  background: transparent;
  border: 0;
  border-radius: calc(var(--ws-r-sm) - 2px);
  padding: 5px 14px;
  font-size: 13px;
  font-weight: 500;
  color: var(--ws-ink-3);
  cursor: pointer;
  font-family: var(--ws-sans);
  transition: background 0.15s, color 0.15s;
}
.ws-auto-scope-tab:hover { color: var(--ws-ink-2); }
.ws-auto-scope-tab.active {
  background: var(--ws-panel);
  color: var(--ws-ink);
  font-weight: 600;
  box-shadow: var(--ws-shadow-sm);
}
.ws-auto-scope-tab.active:hover { color: var(--ws-ink); }
.ws-auto-schedule-editor {
  background: var(--ws-soft);
  border: 1px solid var(--ws-line);
  border-radius: var(--ws-r-sm);
  padding: 14px;
}
/* ScheduleEditor (day picker + time). Self-contained styling so it reads the
   same in every context and never depends on Tailwind grey tokens resolving. */
.ws-sched { display: flex; flex-direction: column; gap: 12px; }
.ws-sched-shortcuts { display: flex; gap: 6px; }
.ws-sched-shortcut {
  background: transparent;
  border: 0;
  padding: 3px 8px;
  border-radius: var(--ws-r-sm);
  font-size: 12px;
  font-weight: 500;
  font-family: var(--ws-sans);
  color: var(--ws-ink-4);
  cursor: pointer;
  transition: color 0.12s, background 0.12s;
}
.ws-sched-shortcut:hover { color: var(--ws-ink-2); background: var(--ws-hover); }
.ws-sched-shortcut.active { color: var(--ws-ink); font-weight: 600; background: var(--ws-hover); }
.ws-sched-row { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
.ws-sched-days { display: inline-flex; gap: 4px; }
.ws-sched-day {
  height: 30px;
  min-width: 42px;
  padding: 0 10px;
  border-radius: var(--ws-r-sm);
  border: 1px solid var(--ws-line-2);
  background: var(--ws-panel);
  color: var(--ws-ink-3);
  font-size: 12.5px;
  font-weight: 500;
  font-family: var(--ws-sans);
  cursor: pointer;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
}
.ws-sched-day:hover { background: var(--ws-soft); color: var(--ws-ink); }
.ws-sched-day.active {
  background: var(--ws-ink);
  border-color: var(--ws-ink);
  color: #fff;
}
.ws-sched-day.active:hover { background: var(--ws-ink-2); color: #fff; }
.ws-sched-time { display: inline-flex; align-items: center; gap: 6px; }
.ws-sched-select {
  height: 30px;
  border-radius: var(--ws-r-sm);
  border: 1px solid var(--ws-line-2);
  background: var(--ws-panel);
  color: var(--ws-ink);
  font-size: 13px;
  font-family: var(--ws-sans);
  padding: 0 8px;
  cursor: pointer;
  outline: none;
  transition: border-color 0.12s;
}
.ws-sched-select:focus { border-color: var(--ws-ink-3); }
.ws-sched-colon { color: var(--ws-ink-4); font-size: 13px; }
.ws-auto-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 4px;
}
.ws-auto-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: var(--ws-r-sm);
  border: 1px solid var(--ws-line);
  background: transparent;
  color: var(--ws-ink-3);
  font-size: 12.5px;
  font-weight: 500;
  font-family: var(--ws-sans);
  cursor: pointer;
  transition: all 0.12s;
}
.ws-auto-action-btn:hover { background: var(--ws-soft); color: var(--ws-ink); border-color: var(--ws-line-2); }
.ws-auto-action-danger:hover { color: var(--ws-err); border-color: rgba(184,74,62,0.3); background: rgba(184,74,62,0.04); }
.ws-auto-confirm-delete {
  display: flex;
  align-items: center;
  gap: 6px;
}
.ws-auto-delete-yes {
  padding: 5px 12px;
  border-radius: var(--ws-r-sm);
  background: var(--ws-err);
  color: white;
  font-size: 12px;
  font-weight: 600;
  font-family: var(--ws-sans);
  border: none;
  cursor: pointer;
}
.ws-auto-delete-yes:hover { opacity: 0.9; }
.ws-auto-delete-no {
  padding: 5px 12px;
  border-radius: var(--ws-r-sm);
  background: transparent;
  color: var(--ws-ink-3);
  font-size: 12px;
  font-weight: 500;
  font-family: var(--ws-sans);
  border: none;
  cursor: pointer;
}
.ws-auto-delete-no:hover { background: var(--ws-soft); }
.ws-auto-save-btn {
  padding: 7px 18px;
  border-radius: var(--ws-r-sm);
  border: 1px solid var(--ws-line);
  background: var(--ws-soft);
  color: var(--ws-ink-4);
  font-size: 13px;
  font-weight: 600;
  font-family: var(--ws-sans);
  cursor: not-allowed;
  transition: all 0.15s;
}
.ws-auto-save-btn.ws-auto-dirty {
  background: var(--ws-ink);
  color: #e8f9f8;
  border-color: var(--ws-ink);
  cursor: pointer;
}
.ws-auto-save-btn.ws-auto-dirty:hover { background: var(--ws-ink-2); }
.ws-auto-save-btn.ws-auto-saved {
  background: var(--ws-ok);
  color: white;
  border-color: var(--ws-ok);
  cursor: default;
}

/* Organization sidebar split item */
.ws-sb-item-main {
  display: flex; align-items: center; gap: 10px;
  flex: 1; min-width: 0;
  border: 0; background: transparent; padding: 0;
  color: inherit; font: inherit; cursor: pointer;
  text-align: left;
}
.ws-sb-item-chev {
  border: 0; background: transparent; padding: 4px;
  cursor: pointer; color: inherit;
  display: grid; place-items: center;
  border-radius: 4px;
  margin-left: auto;
}
.ws-sb-item-chev:hover { background: rgba(0,0,0,0.06); }

/* Nested sidebar items */
.ws-sb-item-nested { padding-left: 32px; }
.ws-sb-item-muted { color: var(--ws-ink-4); font-size: 12.5px; }

/* Routine row leading marker — neutral by default, future home for
   run-status signal (red on error, amber on stale). */
.ws-sb-routine-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--ws-ink-4);
  opacity: 0.5;
  flex-shrink: 0;
  margin: 0 5px;
}

`;
